import {
  BulkInviteMemberItem,
  UpdateMemberUserGroupItem,
} from '../types/request/member-access-request';
import { isInvitable } from '../utils/member-email';
import { MemberBaseline, MemberEdit } from './member-edits.store';

/**
 * Builds the group-update payload over ALL edits (visible or filtered out).
 * Predicate MUST match `mapMemberAccessToUpdateRequest`: only members whose group
 * changed from baseline and whose new group is non-null.
 *
 * Deliberately NOT default-aware: a row left without a group is invited INTO the default group,
 * but its member record keeps no group. Substituting the default here would silently assign it to
 * everyone the admin never touched.
 */
export const buildUpdatePayload = (
  edits: Record<number, MemberEdit>,
  baseline: Record<number, MemberBaseline>
): UpdateMemberUserGroupItem[] =>
  Object.entries(edits)
    .filter(([id, edit]) => {
      const base = baseline[Number(id)];
      return !!base && base.groupId !== edit.groupId && edit.groupId !== null;
    })
    .map(([id, edit]) => ({ teamMemberId: Number(id), userGroupId: Number(edit.groupId) }));

interface BuildInvitePayloadParams {
  edits: Record<number, MemberEdit>;
  /** The organization's default group, applied to any selected row with no explicit group. */
  defaultGroupId: string | null;
}

/**
 * Builds the bulk-invite payload: selected ∧ invitable members across all filters,
 * including any that are currently filtered out of view (selection is keyed by userId).
 *
 * A row with no group falls back to `defaultGroupId`. The bulk endpoint requires a group per
 * member, so resolving it here is what makes the "Default (<name>)" shown in the group cell true.
 */
export const buildInvitePayload = ({
  edits,
  defaultGroupId,
}: BuildInvitePayloadParams): BulkInviteMemberItem[] =>
  Object.entries(edits)
    .filter(
      ([, edit]) =>
        edit.selected &&
        isInvitable({
          groupId: edit.groupId,
          email: edit.email,
          hasDefaultGroup: defaultGroupId !== null,
        })
    )
    .map(([id, edit]) => ({
      teamMemberId: Number(id),
      groupId: Number(edit.groupId ?? defaultGroupId),
      email: edit.email || undefined,
    }));
